/**
 * 考试模块共享字典编码，集中维护可以避免任务创建与成绩查询使用两套分类来源。
 */
export const EXAM_TASK_CATEGORY_DICT_CODE = 'exam_task_category'

/**
 * 考试题型展示顺序，试卷预览、考试过程和答题卡都按该顺序保持一致。
 */
export const EXAM_QUESTION_TYPE_ORDER = ['single', 'multiple', 'judge', 'fill', 'short', 'essay', 'case', 'practical']

/**
 * 考试题型中文名称，避免不同页面各自维护题型文案导致显示不一致。
 */
export const EXAM_QUESTION_TYPE_LABEL: Record<string, string> = {
  single: '单选题',
  multiple: '多选题',
  judge: '判断题',
  fill: '填空题',
  short: '简答题',
  essay: '论述题',
  case: '案例分析',
  practical: '实操题',
}

interface OrderedQuestion {
  questionType?: string
  sortOrder?: number
}

/**
 * 获取题型排序权重，未知题型统一排到已知题型之后。
 *
 * @param type 题型编码
 * @returns 题型排序权重
 */
export function getExamQuestionTypeOrder(type?: string) {
  const index = EXAM_QUESTION_TYPE_ORDER.indexOf(type || '')
  return index >= 0 ? index : EXAM_QUESTION_TYPE_ORDER.length
}

/**
 * 按题型顺序整理试题；需要随机时仅打乱同题型内部顺序，题型大类顺序始终固定。
 *
 * @param questions 待排序试题
 * @param shuffleWithinType 是否在同一题型内随机
 * @returns 排序后的新数组
 */
export function sortExamQuestionsByType<T extends OrderedQuestion>(questions: T[], shuffleWithinType = false): T[] {
  const groups = new Map<string, Array<{ item: T; index: number }>>()
  questions.forEach((item, index) => {
    const type = item.questionType || ''
    const list = groups.get(type) || []
    list.push({ item, index })
    groups.set(type, list)
  })

  return Array.from(groups.entries())
    .sort(([typeA], [typeB]) => getExamQuestionTypeOrder(typeA) - getExamQuestionTypeOrder(typeB))
    .flatMap(([, group]) => {
      const orderedGroup = [...group]
      if (shuffleWithinType) {
        // 只在题型桶内洗牌，防作弊随机题不会破坏单选、多选、判断等大类顺序。
        for (let index = orderedGroup.length - 1; index > 0; index -= 1) {
          const targetIndex = Math.floor(Math.random() * (index + 1))
          const current = orderedGroup[index]
          orderedGroup[index] = orderedGroup[targetIndex]
          orderedGroup[targetIndex] = current
        }
      } else {
        // 非随机场景保留试卷编排顺序；缺少 sortOrder 时使用原数组下标兜底。
        orderedGroup.sort((a, b) => Number(a.item.sortOrder ?? a.index) - Number(b.item.sortOrder ?? b.index))
      }
      return orderedGroup.map(({ item }) => item)
    })
}
